import {
  AnyAutomation,
  AutomationCreator,
  AutomationParentScope,
  IAutomatedFormIssue,
  IAutomatedNotification,
  IAutomation,
  IAutomationScopeRequirements,
  IBrand,
  IEvent,
  IGeneratedTask,
  ITreatmentConfiguration,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  INamedTypeDocument,
  Timestamp,
  WithRef,
  asyncForEach,
  getDoc,
  isSameRef,
  toNamedDocument,
} from '@principle-theorem/shared';
import { compact, uniqWith } from 'lodash';
import { AutomationConfiguration } from '../../automation/automation-configuration';
import { AutomatedFormIssue } from '../../custom-forms/automated-form-issue';
import { AutomatedNotification } from '../../notification/automated-notification';
import { GeneratedTask } from '../../task/generated-task';

export class TreatmentConfigurationAutomations {
  static async convertNotificationsToAutomations(
    configuration: WithRef<ITreatmentConfiguration>,
    event: IEvent,
    brandRef: DocumentReference<IBrand>,
    scopeData: IAutomationScopeRequirements,
    triggerAfterDate?: Timestamp
  ): Promise<IAutomation<IAutomatedNotification>[]> {
    const notifications = await asyncForEach(
      configuration.automatedNotificationRefs,
      async (notificationRef) => {
        const notification = await getDoc(notificationRef);
        if (
          !AutomationConfiguration.isActive(notification) ||
          !AutomationConfiguration.isForPractice(
            notification,
            event.practice.ref
          )
        ) {
          return;
        }
        const creator: INamedTypeDocument<
          ITreatmentConfiguration,
          AutomationCreator
        > = {
          ...toNamedDocument(configuration),
          type: AutomationCreator.TreatmentConfiguration,
        };
        return AutomatedNotification.generateFromConfig(
          notification,
          creator,
          event,
          brandRef,
          scopeData,
          triggerAfterDate
        );
      }
    );
    return compact(notifications);
  }

  static async convertTasksToAutomations(
    configuration: WithRef<ITreatmentConfiguration>,
    event: IEvent,
    brandRef: DocumentReference<IBrand>,
    scopeData: IAutomationScopeRequirements,
    triggerAfterDate?: Timestamp
  ): Promise<IAutomation<IGeneratedTask>[]> {
    const tasks = await asyncForEach(
      configuration.generatedTaskRefs,
      async (taskRef) => {
        const task = await getDoc(taskRef);
        if (
          !AutomationConfiguration.isActive(task) ||
          !AutomationConfiguration.isForPractice(task, event.practice.ref)
        ) {
          return;
        }
        const creator: INamedTypeDocument<
          ITreatmentConfiguration,
          AutomationCreator
        > = {
          ...toNamedDocument(configuration),
          type: AutomationCreator.TreatmentConfiguration,
        };
        return GeneratedTask.generateFromConfig(
          task,
          creator,
          event,
          brandRef,
          scopeData,
          triggerAfterDate
        );
      }
    );
    return compact(tasks);
  }

  static async convertFormIssuesToAutomations(
    configuration: WithRef<ITreatmentConfiguration>,
    event: IEvent,
    brandRef: DocumentReference<IBrand>,
    triggerAfterDate?: Timestamp
  ): Promise<IAutomation<IAutomatedFormIssue>[]> {
    const notifications = await asyncForEach(
      configuration.automatedFormIssueRefs,
      async (formIssueRef) => {
        const formIssue = await Firestore.getDoc(formIssueRef);
        if (
          !AutomationConfiguration.isActive(formIssue) ||
          !AutomationConfiguration.isForPractice(formIssue, event.practice.ref)
        ) {
          return;
        }
        const creator: INamedTypeDocument<
          ITreatmentConfiguration,
          AutomationCreator
        > = {
          ...toNamedDocument(configuration),
          type: AutomationCreator.TreatmentConfiguration,
        };
        return AutomatedFormIssue.generateFromConfig(
          formIssue,
          creator,
          event,
          brandRef,
          triggerAfterDate
        );
      }
    );
    return compact(notifications);
  }

  static async getAutomationsFromConfiguration(
    configuration: WithRef<ITreatmentConfiguration>,
    event: IEvent,
    brandRef: DocumentReference<IBrand>,
    scopeData: IAutomationScopeRequirements,
    triggerAfterDate?: Timestamp
  ): Promise<IAutomation<AnyAutomation>[]> {
    const tasks =
      await TreatmentConfigurationAutomations.convertTasksToAutomations(
        configuration,
        event,
        brandRef,
        scopeData,
        triggerAfterDate
      );
    const notifications =
      await TreatmentConfigurationAutomations.convertNotificationsToAutomations(
        configuration,
        event,
        brandRef,
        scopeData,
        triggerAfterDate
      );
    const formIssues =
      await TreatmentConfigurationAutomations.convertFormIssuesToAutomations(
        configuration,
        event,
        brandRef,
        triggerAfterDate
      );
    return [...tasks, ...notifications, ...formIssues];
  }

  static async getAutomationsFromConfigurations$(
    configurations: WithRef<ITreatmentConfiguration>[],
    event: IEvent,
    brandRef: DocumentReference<IBrand>,
    triggerAfterDate?: Timestamp
  ): Promise<IAutomation<AnyAutomation>[]> {
    const uniqueConfigs = uniqWith(configurations, isSameRef);
    const automations = await asyncForEach(uniqueConfigs, (config) =>
      TreatmentConfigurationAutomations.getAutomationsFromConfiguration(
        config,
        event,
        brandRef,
        {
          parentScope: AutomationParentScope.TreatmentStep,
        },
        triggerAfterDate
      )
    );
    return automations.flat();
  }
}
